import {
  Alert,
  Button,
  VStack,
  Box,
  Text,
  HStack,
  Icon,
  Link,
} from "@chakra-ui/react";
import { capitalizeFirstLetter } from "../../services/Utils";
import useIcons from "../../hooks/useIcons";
import { GOOGLE_BUG_FORM_URL } from "../../services/constants";

interface Props {
  message?: string;
  handleClick?: () => void;
  status?: "error" | "info" | "warning" | "success" | "neutral";
  title?: string;
  useButton?: boolean;
  tight?: boolean;
}

export default function CustomAlert({
  message,
  handleClick,
  status,
  useButton,
  title,
  tight,
}: Props) {
  return (
    <VStack w={"full"} gap={5} align={"stretch"}>
      <Alert.Root status={status || "error"} p={tight ? 1 : 4}>
        <Alert.Indicator />
        <Alert.Title>
          {capitalizeFirstLetter(title || status || "Error")}
        </Alert.Title>
        <Alert.Description>{message || "Something Happened"}</Alert.Description>
      </Alert.Root>
      {useButton && <Button onClick={handleClick}>Retry</Button>}
    </VStack>
  );
}

// --- FullPageError ---

export interface FullPageErrorProps {
  message?: string;
  onRetry?: () => void;
}

export function FullPageError({
  message,
  onRetry = () => window.location.reload(),
}: FullPageErrorProps) {
  const icons = useIcons();
  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      width="100vw"
      height="100svh"
      zIndex={2000}
      display="flex"
      alignItems="center"
      justifyContent="center"
      backdropFilter="blur(10px)"
    >
      <Box
        borderRadius="2xl"
        boxShadow="2xl"
        p={8}
        maxW="500px"
        w="90%"
        textAlign="center"
        borderWidth={1}
        position="relative"
        overflow="hidden"
      >
        <VStack gap={6} position="relative" zIndex={1}>
          {/* Icon */}
          <Box
            w="80px"
            h="80px"
            borderRadius="full"
            // bgGradient="linear(to-br, red.400, red.600)"
            bgGradient="to-br"
            gradientFrom="red.400"
            gradientTo="red.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="lg"
          >
            <Icon as={icons.utility.error} w={8} h={8} color="white" />
          </Box>

          {/* Message */}
          <Text fontSize="lg" lineHeight="1.6" maxW="400px">
            {message ||
              "We couldn't connect to the server. Please check your internet connection and try again."}
          </Text>

          {/* Action Buttons */}
          <HStack gap={4} pt={2}>
            <Button
              size={"lg"}
              colorPalette="red"
              onClick={onRetry}
              fontWeight="bold"
              boxShadow="md"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg",
              }}
              transition="all 0.2s"
            >
              <Icon as={icons.utility.refresh} mr={2} />
              Retry Connection
            </Button>
          </HStack>

          {/* Additional Help Text */}
          <Text fontSize="sm" color="gray.500" pt={2}>
            If the problem persists, please{" "}
            <Link
              target="_blank"
              _hover={{ textDecoration: "underline" }}
              href={GOOGLE_BUG_FORM_URL}
            >
              report
            </Link>{" "}
            the issue.
          </Text>
        </VStack>
      </Box>
    </Box>
  );
}
